import {
  Card,
  Stack,
  Text,
  ThemeIcon,
  Button,
  Table,
  Badge,
  ActionIcon,
  Group,
  Menu,
  ThemeIcon as MantineThemeIcon,
} from '@mantine/core';
import {
  Calendar,
  Users,
  MoreVertical,
  Edit3,
  Flag,
  MessageCircle,
} from 'react-feather';
import type { Session } from '../../../../types';
import type { SessionWithDetailsAndRsvp } from '../../../../services/api/sessions';
import { formatDate, formatTime } from '../../../../utils/formatters';
import { TopicPill } from './index';
import { getDisplayStatus } from './sessionsHelpers';
import { SessionContentStatusBadge } from './SessionContentStatusBadge';

interface NoGroupsZeroStateProps {
  onNavigateToParticipants?: () => void;
}

/**
 * Zero state when no groups have been created at all.
 * Prompts user to upload matching tool CSV.
 */
export function NoGroupsZeroState({ onNavigateToParticipants }: NoGroupsZeroStateProps) {
  return (
    <Card withBorder p="xl" style={{ borderStyle: 'dashed' }}>
      <Stack align="center" gap="md">
        <ThemeIcon size={60} radius={60} variant="light" color="gray">
          <Users size={30} />
        </ThemeIcon>
        <Stack align="center" gap="xs">
          <Text fw={500}>No groups created yet</Text>
          <Text size="sm" c="dimmed" ta="center" maw={400}>
            Upload the matching tool CSV in the Participants tab to create groups and
            auto-generate sessions.
          </Text>
        </Stack>
        {onNavigateToParticipants && (
          <Button variant="light" onClick={onNavigateToParticipants} mt="sm">
            Go to Participants &amp; Groups
          </Button>
        )}
      </Stack>
    </Card>
  );
}

interface NoSessionsZeroStateProps {
  groupCount: number;
}

/**
 * Zero state when groups exist but no sessions have been generated yet.
 * This happens when groups lack time slots or the cohort lacks start/end dates.
 */
export function NoSessionsZeroState({ groupCount }: NoSessionsZeroStateProps) {
  return (
    <Card withBorder p="xl" style={{ borderStyle: 'dashed' }}>
      <Stack align="center" gap="md">
        <ThemeIcon size={60} radius={60} variant="light" color="blue">
          <Calendar size={30} />
        </ThemeIcon>
        <Stack align="center" gap="xs">
          <Text fw={500}>No Sessions Generated Yet</Text>
          <Text size="sm" c="dimmed" ta="center" maw={400}>
            {groupCount} group{groupCount !== 1 ? 's' : ''} created. Sessions will be
            auto-generated when groups have time slots assigned and the cohort has start/end
            dates.
          </Text>
        </Stack>
      </Stack>
    </Card>
  );
}

interface FlatSessionListProps {
  sessions: SessionWithDetailsAndRsvp[];
  onSessionClick: (session: Session) => void;
  onOpenTopicModal: (session: SessionWithDetailsAndRsvp) => void;
}

/**
 * Flat list view for non-group-coaching cohorts that shows sessions
 * without group grouping.
 */
export function FlatSessionList({
  sessions,
  onSessionClick,
  onOpenTopicModal,
}: FlatSessionListProps) {
  const sorted = [...sessions].sort(
    (a, b) =>
      new Date(a.scheduled_start || 0).getTime() - new Date(b.scheduled_start || 0).getTime()
  );

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>#</Table.Th>
          <Table.Th>Topic</Table.Th>
          <Table.Th>Title</Table.Th>
          <Table.Th>Date</Table.Th>
          <Table.Th>Time</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th></Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {sorted.map((session) => {
          const displayStatus = getDisplayStatus(session);

          return (
            <Table.Tr
              key={session.id}
              onClick={() => onSessionClick(session)}
              style={{ cursor: 'pointer' }}
            >
              <Table.Td>{session.session_number || '—'}</Table.Td>
              <Table.Td>
                <TopicPill
                  topicName={session.topic_name}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenTopicModal(session);
                  }}
                />
              </Table.Td>
              <Table.Td>
                {/* MED-1075 / E1.3k: content badge inlined beside the title (left
                    side, AC8). Renders nothing for non-group-coaching sessions, so
                    no empty column is added to this non-group-coaching list. */}
                <Group gap="xs" wrap="nowrap">
                  <span>{session.title}</span>
                  <SessionContentStatusBadge contentStatus={session.content_status} />
                </Group>
              </Table.Td>
              <Table.Td>{formatDate(session.scheduled_start)}</Table.Td>
              <Table.Td>{formatTime(session.scheduled_start)}</Table.Td>
              <Table.Td>
                <Group gap="xs">
                  <Badge size="sm" color={displayStatus.color} variant="light">
                    {displayStatus.label}
                  </Badge>
                  {(session as unknown as { feedback_submitted?: boolean }).feedback_submitted && (
                    <MantineThemeIcon
                      size="xs"
                      variant="light"
                      color="green"
                      title="Coach feedback submitted"
                    >
                      <MessageCircle size={10} />
                    </MantineThemeIcon>
                  )}
                  {((session as unknown as { open_concern_count?: number }).open_concern_count ??
                    0) > 0 && (
                    <Badge
                      size="xs"
                      variant="light"
                      color="orange"
                      leftSection={<Flag size={8} />}
                    >
                      {(session as unknown as { open_concern_count: number }).open_concern_count}
                    </Badge>
                  )}
                </Group>
              </Table.Td>
              <Table.Td>
                <Menu position="bottom-end" withinPortal>
                  <Menu.Target>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical size={14} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<Edit3 size={14} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSessionClick(session);
                      }}
                    >
                      Edit Session
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}
